import { useCallback, useEffect, useRef, useState } from 'react';
import { createStore, useStore } from 'zustand';
import useBasket from '~/features/commerce-api/hooks/useBasket';
import { clientLog } from '~/lib/better-stack';
import { MissingBasketError } from '~/services/altapay/lib/errors';
import { ClientSideSingleton as AltaPaySession } from '~/services/altapay/session';
import { AltaPay } from '~/services/altapay/types';
import { useFrame } from '~/shared/utils';
import { parseError } from '~/shared/utils/parse-error';

interface Config {
    enabled?: boolean;
}

type SessionStatus = 'idle' | 'loading' | 'ready' | 'failed';

interface AltaPaySessionState {
    session: AltaPay.Schemas['Session'] | null;
    status: SessionStatus;
    error: unknown | null;
}

interface AltaPaySessionActions {
    reset(): void;
}

const initialState: AltaPaySessionState = {
    session: null,
    status: 'idle',
    error: null,
};

const altaPaySessionStore = createStore<AltaPaySessionState & AltaPaySessionActions>((set) => ({
    ...initialState,

    reset: () => set(initialState),
}));

export const useAltaPayStore = () => useStore(altaPaySessionStore);

export const resetAltaPaySession = () => {
    AltaPaySession.clearSession();
    altaPaySessionStore.getState().reset();
};

const startSession = (basketId: string | undefined) => {
    altaPaySessionStore.setState({ status: 'loading', error: null });

    return AltaPaySession.initialize()
        .then((session) => altaPaySessionStore.setState({ session, status: 'ready' }))
        .catch((error) => {
            altaPaySessionStore.setState({ error, status: 'failed' });

            clientLog({
                event: 'AltaPay:Checkout:SessionInitError',
                message: 'A session with AltaPay cannot be started due to technical errors.',
                data: { error: parseError(error), basketId },
            });
        });
};

export const useAltapaySession = (config?: Config) => {
    const { data: frame } = useFrame();
    const { session, error } = useAltaPayStore();
    const currentBasket = useBasket();
    const { refreshBasket } = currentBasket;

    const { enabled = true } = config || {};

    const [isUpdatingSession, setIsUpdatingSession] = useState(false);

    const basketTimestamp = useRef<string | null>(null);

    useEffect(() => {
        // More than one component uses this hook. Only the first one may start the session.
        if (enabled && altaPaySessionStore.getState().status === 'idle') {
            startSession(currentBasket.basketId);
        }
    }, [enabled]);

    const updateSession = useCallback(
        (payload: Partial<AltaPay.Schemas['Session']>) => {
            setIsUpdatingSession(true);

            const request = AltaPaySession.authorizedRequest<void, AltaPay.Schemas['Session']>(
                `/session/${session?.sessionId}`,
                {
                    method: 'PUT',
                    body: payload,
                },
            );

            // Merge into the latest session. 'session' here can be stale after an earlier update.
            return request
                .then(() =>
                    altaPaySessionStore.setState((state) => ({
                        session: { ...state.session, ...payload },
                    })),
                )
                .finally(() => setIsUpdatingSession(false));
        },
        [session?.sessionId],
    );

    const syncSessionWithBasket = async (
        orderId: string,
        session?: Partial<AltaPay.Schemas['Session']>,
        shouldUseCurrentBasket?: boolean,
    ) => {
        /**
         * As our basket data from our store can go "stale", we will need to fetch the customer's
         * basket data each and every time we attempt to sync our AltaPay session.
         *
         * We can still use 'shouldUseCurrentBasket' to use the current basket data to save bandwidth.
         */
        const basket = shouldUseCurrentBasket ? currentBasket : await refreshBasket();

        if (!basket) throw new MissingBasketError();

        const {
            shipments,
            customerInfo,
            productItems,
            c_remainingTotal: basketTotal,
            billingAddress,
            lastModified,
        } = basket;

        const noChangesInBasket =
            !!basketTimestamp.current && basketTimestamp.current === lastModified;

        /**
         * We will only update our AltaPay session if our basket timestamp no longer matches
         * the saved timestamp. Or, if a configuration object was passed.
         */
        if (noChangesInBasket && !session?.configuration) return;

        const shipmentAddress = shipments?.[0]?.shippingAddress;
        const currency = frame.market.currency?.currency ?? 'DKK';

        basketTimestamp.current = String(lastModified);

        await updateSession({
            order: {
                orderId,
                amount: {
                    value: basketTotal ?? 0,
                    currency,
                },
                orderLines: productItems?.map((product) => ({
                    itemId: product.productId ?? '',
                    description: product.productName ?? '',
                    quantity: product.quantity ?? 0,
                    unitPrice:
                        product.priceAfterOrderDiscount ||
                        product.priceAfterItemDiscount ||
                        product.price ||
                        0,
                })),
                customer: {
                    firstName: billingAddress?.firstName,
                    lastName: billingAddress?.lastName,
                    email: customerInfo?.email,
                    phoneNumber: billingAddress?.phone,
                    billingAddress: {
                        street: billingAddress?.address1 ?? '',
                        city: billingAddress?.city ?? '',
                        country: billingAddress?.countryCode ?? '',
                        zipCode: billingAddress?.postalCode ?? '',
                    },
                    shippingAddress: shipmentAddress && {
                        street: shipmentAddress.address1 ?? '',
                        city: shipmentAddress.city ?? '',
                        country: shipmentAddress.countryCode ?? '',
                        zipCode: shipmentAddress.postalCode ?? '',
                    },
                },
                // We will need this for our /notification and /verify endpoints.
                transactionInfo: { basketId: basket.basketId ?? '' },
            },
            ...session,
        });
    };

    const getPaymentMethods = useCallback(() => {
        const request = AltaPaySession.authorizedRequest<AltaPay.Schemas['PaymentMethodResponse']>(
            `/session/${session?.sessionId}/payment-methods`,
        );

        return request.then((response) => response.methods);
    }, [session?.sessionId]);

    return {
        error,
        isUpdatingSession,
        sessionId: session?.sessionId,
        syncSessionWithBasket,
        getPaymentMethods,
    };
};
